import React, { useEffect, useCallback, useState, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import ShieldImg from "../../Assets/Icons/shield.png";
import FucaScoreLogo from "../../Assets/Images/fucascore_png.png";
import LoaderBox from "./LoaderBox";

const LED_DIGITS = {
  0: ["01110", "11011", "11011", "11011", "11011", "11011", "01110"],
  1: ["00110", "01110", "00110", "00110", "00110", "00110", "01111"],
  2: ["01110", "11011", "00011", "00110", "01100", "11000", "11111"],
  3: ["11110", "00011", "00011", "01110", "00011", "00011", "11110"],
  4: ["11011", "11011", "11011", "11111", "00011", "00011", "00011"],
  5: ["11111", "11000", "11000", "11110", "00011", "00011", "11110"],
  6: ["01110", "11000", "11000", "11110", "11011", "11011", "01110"],
  7: ["11111", "00011", "00110", "00110", "01100", "01100", "01100"],
  8: ["01110", "11011", "11011", "01110", "11011", "11011", "01110"],
  9: ["01110", "11011", "11011", "01111", "00011", "00011", "01110"],
};

const LED_LETTERS = {
  G: ["01110", "11000", "11000", "11011", "11011", "11011", "01110"],
  O: ["01110", "11011", "11011", "11011", "11011", "11011", "01110"],
  L: ["11000", "11000", "11000", "11000", "11000", "11000", "11111"],
};

const LEDCharacter = ({ char, charIndex = 0 }) => {
  const pattern =
    LED_DIGITS[char] !== undefined
      ? LED_DIGITS[char]
      : LED_LETTERS[char] || LED_DIGITS[0];
  const prevCharRef = useRef(char);
  const [isChanging, setIsChanging] = useState(false);

  useEffect(() => {
    if (prevCharRef.current !== char) {
      setIsChanging(true);
      const timer = setTimeout(() => setIsChanging(false), 500);
      prevCharRef.current = char;
      return () => clearTimeout(timer);
    }
  }, [char]);

  return (
    <div
      className={`led-digit ${isChanging ? "digit-changing" : ""}`}
      aria-label={char}
    >
      {pattern.flatMap((row, rowIndex) =>
        [...row].map((pixel, colIndex) => (
          <span
            key={`${rowIndex}-${colIndex}`}
            className={`led-pixel ${pixel === "1" ? "on" : ""}`}
            style={{
              // Kombiniramo odmak po slovu i odmak unutar slova za savršen val točkica
              transitionDelay: `${charIndex * 100 + (rowIndex + colIndex) * 12}ms`,
            }}
          />
        )),
      )}
    </div>
  );
};

const HomeLiveTV = () => {
  const { turnirSlug } = useParams();
  const [match, setMatch] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [tournamentName, setTournamentName] = useState("FucaScore");

  // Stanja za GOOL animaciju
  const [displayMode, setDisplayMode] = useState("SCORE"); // "SCORE" ili "GOAL"
  const prevScoresRef = useRef({ a: 0, b: 0 });
  const isFirstLoadRef = useRef(true);

  const fetchLiveMatch = useCallback(
    async (isInitial = false) => {
      try {
        if (isInitial) setInitialLoading(true);

        const { data: turnirData } = await supabase
          .from("admin_turniri")
          .select("id, naziv")
          .eq("slug", turnirSlug)
          .maybeSingle();

        if (!turnirData) {
          setMatch(null);
          return;
        }

        setTournamentName(turnirData.naziv || "FucaScore");
        const tid = turnirData.id;

        let live = null,
          isVet = false;
        const { data: reg } = await supabase
          .from("turniri_utakmice")
          .select(`*, team_a:team_a_id(*), team_b:team_b_id(*)`)
          .eq("tournament_id", tid)
          .eq("is_live", true)
          .maybeSingle();

        if (reg) {
          live = reg;
        } else {
          const { data: vet } = await supabase
            .from("turniri_veterani")
            .select(`*, team_a:team_a_id(*), team_b:team_b_id(*)`)
            .eq("tournament_id", tid)
            .eq("is_live", true)
            .maybeSingle();
          if (vet) {
            live = vet;
            isVet = true;
          }
        }

        const newMatch = live ? { ...live, isVeteran: isVet } : null;

        // Detekcija promjene rezultata (GOL!)
        if (newMatch) {
          const newA = newMatch.scorea ?? 0;
          const newB = newMatch.scoreb ?? 0;

          if (!isFirstLoadRef.current) {
            if (
              newA > prevScoresRef.current.a ||
              newB > prevScoresRef.current.b
            ) {
              setDisplayMode("GOAL");
              setTimeout(() => {
                setDisplayMode("SCORE");
              }, 4500); // GOOOL! natpis traje 4.5 sekundi pa se vraća rezultat
            }
          }

          prevScoresRef.current = { a: newA, b: newB };
          isFirstLoadRef.current = false;
        }

        setMatch(newMatch);
      } catch (err) {
        console.error(err);
      } finally {
        if (isInitial) setInitialLoading(false);
      }
    },
    [turnirSlug],
  );

  useEffect(() => {
    fetchLiveMatch(true);

    const ch1 = supabase
      .channel(`tv-reg-${turnirSlug}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "turniri_utakmice" },
        () => fetchLiveMatch(false),
      )
      .subscribe();

    const ch2 = supabase
      .channel(`tv-vet-${turnirSlug}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "turniri_veterani" },
        () => fetchLiveMatch(false),
      )
      .subscribe();

    return () => {
      supabase.removeChannel(ch1);
      supabase.removeChannel(ch2);
    };
  }, [turnirSlug, fetchLiveMatch]);

  if (initialLoading)
    return <LoaderBox text="Pokretanje FucaScore LIVE TV-a..." />;

  if (!match) {
    return (
      <section className="fucascore-tv tv-idle">
        <div className="tv-background-glow" />
        <header className="tv-header">
          <Link to="/" className="tv-brand">
            <img
              src={FucaScoreLogo}
              alt="FucaScore logo"
              className="tv-brand-logo"
            />
          </Link>
          <div className="tv-tournament">{tournamentName}</div>
        </header>
        <main className="tv-idle-content">
          <div className="idle-icon">
            <span className="idle-ring ring-one" />
            <span className="idle-ring ring-two" />
            <span className="idle-ring ring-three" />
            <img
              src={FucaScoreLogo}
              alt="FucaScore logo"
              className="idle-ball"
            />
          </div>
          <span className="idle-kicker">FUCASCORE LIVE TV</span>
          <h1>
            ČEKANJE NA <strong>UTAKMICU</strong>
          </h1>
          <p>
            Trenutno nema aktivne utakmice uživo. Prijenos će se automatski
            pokrenuti kada utakmica započne.
          </p>
        </main>
        <footer className="tv-footer">
          <h5>fucascore.com</h5>
        </footer>
      </section>
    );
  }

  const { team_a: tA, team_b: tB } = match;
  const sA = match.scorea ?? 0,
    sB = match.scoreb ?? 0;

  return (
    <section
      className={`fucascore-tv tv-live ${displayMode === "GOAL" ? "goal-flash" : ""}`}
    >
      <div className="tv-background-glow glow-green" />
      <div className="tv-background-glow glow-cyan" />

      {/* Header */}
      <header className="tv-header">
        <Link to="/" className="tv-brand">
          <img
            src={FucaScoreLogo}
            alt="FucaScore logo"
            className="tv-brand-logo"
          />
        </Link>
        <div className="tv-tournament">
          {tournamentName}{" "}
          {match.isVeteran && <span className="category-badge">VETERANI</span>}
        </div>
      </header>

      {/* Glavni dio - Semafor */}
      <main className="tv-match-area">
        <div className="scoreboard">
          <div
            className={`tv-status ${displayMode === "GOAL" ? "goal-status" : ""}`}
          >
            <span />
            {displayMode === "GOAL" ? "GOL!" : "UŽIVO"}
          </div>

          <div className="tv-team team-a">
            <div className="team-logo-wrapper">
              <div className="logo-glow" />
              <img src={tA?.logo_url || ShieldImg} alt={tA?.name} />
            </div>
            <div className="team-name">{tA?.name || "Tim A"}</div>
          </div>

          <div className="score-center">
            {displayMode === "GOAL" ? (
              <div className="goal-row">
                <LEDCharacter char="G" charIndex={0} />
                <LEDCharacter char="O" charIndex={1} />
                <LEDCharacter char="O" charIndex={2} />
                <LEDCharacter char="L" charIndex={3} />
              </div>
            ) : (
              <div className="score-row">
                <LEDCharacter char={sA} charIndex={0} />
                <span className="score-divider">:</span>
                <LEDCharacter char={sB} charIndex={1} />
              </div>
            )}

            <div className="match-state">
              <span className="state-dot" />
              {displayMode === "GOAL"
                ? "NEVJEROJATAN POGODAK!"
                : match.half || "1. POLUVRIJEME"}
            </div>
          </div>

          <div className="tv-team team-b">
            <div className="team-logo-wrapper">
              <div className="logo-glow" />
              <img src={tB?.logo_url || ShieldImg} alt={tB?.name} />
            </div>
            <div className="team-name">{tB?.name || "Tim B"}</div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="tv-footer">
        <h5>fucascore.com</h5>
      </footer>
    </section>
  );
};

export default HomeLiveTV;
